import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { 
  Settings,
  Mail,
  Bell,
  Loader2,
  Save,
  ScanEye,
  Sliders,
  KeyRound,
  Eye,
  EyeOff,
  Shield,
  Clock,
  Calendar,
  Play
} from "lucide-react";
import { API } from "@/App";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TwoFactorStatus } from "@/components/TwoFactorAuth";

const DAYS_OF_WEEK = [
  { value: "mon", label: "Monday" },
  { value: "tue", label: "Tuesday" },
  { value: "wed", label: "Wednesday" },
  { value: "thu", label: "Thursday" },
  { value: "fri", label: "Friday" },
  { value: "sat", label: "Saturday" },
  { value: "sun", label: "Sunday" }
];

export const SettingsPage = () => {
  const { user, changePassword } = useAuth();
  const [settings, setSettings] = useState({
    email_notifications_enabled: false,
    notification_email: "",
    similarity_threshold: 0.6,
    auto_scan_enabled: true,
    scan_frequency: "daily"
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordRequirements, setPasswordRequirements] = useState(null);
  const [twoFactorRefresh, setTwoFactorRefresh] = useState(0);
  
  // Automation schedule state
  const [automationSchedule, setAutomationSchedule] = useState({
    day_of_week: "fri",
    hour: 18,
    minute: 0,
    enabled: true,
    timezone: "Europe/London",
    next_scheduled_run: null
  });
  const [savingAutomation, setSavingAutomation] = useState(false);
  const [runningAutomation, setRunningAutomation] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes, reqRes] = await Promise.all([
          axios.get(`${API}/settings`),
          axios.get(`${API}/auth/password-requirements`)
        ]);
        setSettings(settingsRes.data);
        setPasswordRequirements(reqRes.data);
        
        // Fetch automation schedule for admin users
        if (user?.role === 'super_admin' || user?.role === 'admin') {
          try {
            const automationRes = await axios.get(`${API}/settings/automation-schedule`);
            setAutomationSchedule(automationRes.data);
          } catch (e) {
            console.log("Automation settings not available");
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load settings");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  const validatePassword = (pwd) => {
    const errors = [];
    if (passwordRequirements) {
      if (pwd.length < passwordRequirements.min_length) {
        errors.push(`At least ${passwordRequirements.min_length} characters`);
      }
      if (passwordRequirements.require_uppercase && !/[A-Z]/.test(pwd)) {
        errors.push('One uppercase letter');
      }
      if (passwordRequirements.require_lowercase && !/[a-z]/.test(pwd)) {
        errors.push('One lowercase letter');
      }
      if (passwordRequirements.require_digit && !/\d/.test(pwd)) {
        errors.push('One number');
      }
      if (passwordRequirements.require_special && !/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) {
        errors.push('One special character');
      }
    }
    return errors;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, settings);
      toast.success("Settings saved successfully");
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }
    
    const passwordErrors = validatePassword(newPassword);
    if (passwordErrors.length > 0) {
      toast.error(`Password requirements not met: ${passwordErrors.join(', ')}`);
      return;
    }
    
    setChangingPassword(true);
    try {
      await changePassword(currentPassword, newPassword);
      toast.success("Password changed successfully");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSaveAutomation = async () => {
    setSavingAutomation(true);
    try {
      const response = await axios.put(`${API}/settings/automation-schedule`, {
        day_of_week: automationSchedule.day_of_week,
        hour: automationSchedule.hour,
        minute: automationSchedule.minute,
        enabled: automationSchedule.enabled,
        timezone: automationSchedule.timezone
      });
      setAutomationSchedule(prev => ({
        ...prev,
        next_scheduled_run: response.data.next_scheduled_run
      }));
      toast.success("Automation schedule saved successfully");
    } catch (error) {
      console.error("Error saving automation schedule:", error);
      toast.error(error.response?.data?.detail || "Failed to save automation schedule");
    } finally {
      setSavingAutomation(false);
    }
  };

  const handleRunNow = async () => {
    setRunningAutomation(true);
    try {
      await axios.post(`${API}/settings/automation-schedule/run-now`);
      toast.success("Automation process triggered! Check UK IPO Data page for progress.");
    } catch (error) {
      console.error("Error running automation:", error);
      toast.error(error.response?.data?.detail || "Failed to trigger automation");
    } finally {
      setRunningAutomation(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="settings-loading">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div data-testid="settings-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-heading font-black text-slate-900 tracking-tight">
          Settings
        </h1>
        <p className="text-slate-500 mt-1">Configure your monitoring preferences</p>
      </div>

      <div className="max-w-2xl space-y-6">
        {/* Email Notifications */}
        <motion.div 
          className="swiss-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-slate-100 flex items-center justify-center">
              <Mail className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-lg text-slate-900">Email Notifications</h2>
              <p className="text-sm text-slate-500">Receive alerts via email</p>
            </div>
          </div>

          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-semibold text-slate-700">Enable Email Notifications</Label>
                <p className="text-sm text-slate-500 mt-1">
                  Get notified when potential infringements are detected
                </p>
              </div>
              <Switch
                checked={settings.email_notifications_enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, email_notifications_enabled: checked })}
                data-testid="email-notifications-toggle"
              />
            </div>

            <div>
              <Label className="text-sm font-semibold text-slate-700 uppercase tracking-wider">
                Notification Email
              </Label>
              <Input
                type="email"
                value={settings.notification_email}
                onChange={(e) => setSettings({ ...settings, notification_email: e.target.value })}
                className="input-swiss mt-2"
                placeholder="your@email.com"
                disabled={!settings.email_notifications_enabled}
                data-testid="notification-email-input"
              />
              <p className="text-xs text-slate-400 mt-2">
                Alerts will be sent to this email address
              </p>
            </div>
          </div>
        </motion.div>

        {/* Scanning Settings */}
        <motion.div 
          className="swiss-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-slate-100 flex items-center justify-center">
              <ScanEye className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-lg text-slate-900">Scanning Settings</h2>
              <p className="text-sm text-slate-500">Configure monitoring behavior</p>
            </div>
          </div>

          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-semibold text-slate-700">Auto Scan</Label>
                <p className="text-sm text-slate-500 mt-1">
                  Automatically scan for new trademark applications
                </p>
              </div>
              <Switch
                checked={settings.auto_scan_enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, auto_scan_enabled: checked })}
                data-testid="auto-scan-toggle"
              />
            </div>

            <div>
              <Label className="text-sm font-semibold text-slate-700 uppercase tracking-wider">
                Scan Frequency
              </Label>
              <Select 
                value={settings.scan_frequency} 
                onValueChange={(value) => setSettings({ ...settings, scan_frequency: value })}
                disabled={!settings.auto_scan_enabled}
              >
                <SelectTrigger className="input-swiss mt-2" data-testid="scan-frequency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hourly">Hourly</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </motion.div>

        {/* Similarity Threshold */}
        <motion.div 
          className="swiss-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-slate-100 flex items-center justify-center">
              <Sliders className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-lg text-slate-900">Detection Sensitivity</h2>
              <p className="text-sm text-slate-500">Adjust similarity threshold for alerts</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold text-slate-700">Similarity Threshold</Label>
              <span className="font-mono text-lg font-bold text-slate-900">
                {Math.round(settings.similarity_threshold * 100)}%
              </span>
            </div>
            <Slider
              value={[settings.similarity_threshold * 100]}
              onValueChange={([value]) => setSettings({ ...settings, similarity_threshold: value / 100 })}
              min={30}
              max={90}
              step={5}
              className="w-full"
              data-testid="similarity-threshold-slider"
            />
            <div className="flex justify-between text-xs text-slate-400">
              <span>More Sensitive (30%)</span>
              <span>Less Sensitive (90%)</span>
            </div>
            <p className="text-sm text-slate-500">
              Lower values will trigger more alerts, higher values will only alert on very similar marks
            </p>
          </div>
        </motion.div>

        {/* Save Button for App Settings */}
        <div className="pt-4">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="btn-primary flex items-center gap-2"
            data-testid="save-settings-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {saving ? "SAVING..." : "SAVE SETTINGS"}
          </Button>
        </div>

        {/* Account Security Section */}
        <motion.div 
          className="swiss-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-slate-100 flex items-center justify-center">
              <KeyRound className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-lg text-slate-900">Account Security</h2>
              <p className="text-sm text-slate-500">Change your password</p>
            </div>
          </div>

          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <Label className="text-sm font-semibold text-slate-700 uppercase tracking-wider">
                Current Password
              </Label>
              <div className="relative mt-2">
                <Input
                  type={showCurrentPassword ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="input-swiss pr-10"
                  placeholder="Enter current password"
                  required
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label className="text-sm font-semibold text-slate-700 uppercase tracking-wider">
                New Password
              </Label>
              <div className="relative mt-2">
                <Input
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="input-swiss pr-10"
                  placeholder="Enter new password"
                  required
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-slate-400 mt-1">
                {passwordRequirements?.requirements || "Minimum 8 characters, 1 uppercase, 1 lowercase, 1 number"}
              </p>
            </div>

            <div>
              <Label className="text-sm font-semibold text-slate-700 uppercase tracking-wider">
                Confirm New Password
              </Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input-swiss mt-2"
                placeholder="Confirm new password"
                required
                data-testid="confirm-password-input"
              />
            </div>

            <div className="pt-2">
              <Button
                type="submit"
                disabled={changingPassword}
                className="btn-primary flex items-center gap-2"
                data-testid="change-password-btn"
              >
                {changingPassword ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <KeyRound className="w-4 h-4" />
                )}
                {changingPassword ? "CHANGING..." : "CHANGE PASSWORD"}
              </Button>
            </div>
          </form>
        </motion.div>

        {/* Two-Factor Authentication Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
        >
          <TwoFactorStatus refreshTrigger={twoFactorRefresh} />
        </motion.div>

        {/* Automation Schedule Section - Admin Only */}
        {(user?.role === 'super_admin' || user?.role === 'admin') && (
          <motion.div 
            className="swiss-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-purple-100 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900">Automated Monitoring Schedule</h2>
                <p className="text-sm text-slate-500">Configure when to automatically fetch UK IPO data and run scans</p>
              </div>
            </div>

            <div className="space-y-6">
              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between p-4 bg-slate-50 border border-slate-200">
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-slate-500" />
                  <div>
                    <Label className="text-sm font-semibold text-slate-700">Enable Automation</Label>
                    <p className="text-xs text-slate-500">Automatically fetch journals and run infringement scans</p>
                  </div>
                </div>
                <Switch
                  checked={automationSchedule.enabled}
                  onCheckedChange={(checked) => setAutomationSchedule(prev => ({ ...prev, enabled: checked }))}
                  data-testid="automation-enabled-switch"
                />
              </div>

              {automationSchedule.enabled && (
                <>
                  {/* Day Selection */}
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 block mb-2">Day of Week</Label>
                    <Select 
                      value={automationSchedule.day_of_week} 
                      onValueChange={(val) => setAutomationSchedule(prev => ({ ...prev, day_of_week: val }))}
                    >
                      <SelectTrigger className="input-swiss" data-testid="automation-day-select">
                        <SelectValue placeholder="Select day" />
                      </SelectTrigger>
                      <SelectContent>
                        {DAYS_OF_WEEK.map(day => (
                          <SelectItem key={day.value} value={day.value}>{day.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-400 mt-1">UK IPO publishes new journals every Friday</p>
                  </div>

                  {/* Time Selection */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-semibold text-slate-700 block mb-2">Hour (24h format)</Label>
                      <Select 
                        value={automationSchedule.hour.toString()} 
                        onValueChange={(val) => setAutomationSchedule(prev => ({ ...prev, hour: parseInt(val) }))}
                      >
                        <SelectTrigger className="input-swiss" data-testid="automation-hour-select">
                          <SelectValue placeholder="Hour" />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 24 }, (_, i) => (
                            <SelectItem key={i} value={i.toString()}>{i.toString().padStart(2, '0')}:00</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm font-semibold text-slate-700 block mb-2">Minute</Label>
                      <Select 
                        value={automationSchedule.minute.toString()} 
                        onValueChange={(val) => setAutomationSchedule(prev => ({ ...prev, minute: parseInt(val) }))}
                      >
                        <SelectTrigger className="input-swiss" data-testid="automation-minute-select">
                          <SelectValue placeholder="Minute" />
                        </SelectTrigger>
                        <SelectContent>
                          {[0, 15, 30, 45].map(m => (
                            <SelectItem key={m} value={m.toString()}>{m.toString().padStart(2, '0')}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Next Run Info */}
                  {automationSchedule.next_scheduled_run && (
                    <div className="p-3 bg-emerald-50 border border-emerald-200">
                      <p className="text-sm text-emerald-700">
                        <strong>Next scheduled run:</strong>{" "}
                        {new Date(automationSchedule.next_scheduled_run).toLocaleString('en-GB', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          timeZoneName: 'short'
                        })}
                      </p>
                    </div>
                  )}
                </>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-slate-200">
                <Button
                  onClick={handleSaveAutomation}
                  disabled={savingAutomation}
                  className="btn-primary flex items-center gap-2"
                  data-testid="save-automation-btn"
                >
                  {savingAutomation ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {savingAutomation ? "SAVING..." : "SAVE SCHEDULE"}
                </Button>
                <Button
                  onClick={handleRunNow}
                  disabled={runningAutomation}
                  variant="outline"
                  className="flex items-center gap-2"
                  data-testid="run-now-btn"
                >
                  {runningAutomation ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Play className="w-4 h-4" />
                  )}
                  {runningAutomation ? "RUNNING..." : "RUN NOW"}
                </Button>
              </div>

              {/* Info Note */}
              <div className="p-3 bg-amber-50 border border-amber-200 text-sm text-amber-800">
                <strong>What happens during automation:</strong>
                <ol className="list-decimal list-inside mt-2 space-y-1">
                  <li>Fetches the latest UK IPO Trade Marks Journal</li>
                  <li>Imports all new trademark applications</li>
                  <li>Runs infringement scan against your registered trademarks</li>
                  <li>Creates alerts for any potential conflicts</li>
                </ol>
              </div>
            </div>
          </motion.div>
        )}

        {/* Info Box */}
        <motion.div 
          className="swiss-card bg-blue-50 border-blue-200"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <div className="flex items-start gap-3">
            <Bell className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-semibold text-blue-900">Email Configuration Note</p>
              <p className="text-sm text-blue-700 mt-1">
                Email notifications require SendGrid API configuration on the server. 
                Contact your administrator if emails are not being received.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};
